import { Alert, Box, Button, Card, CardContent, Skeleton, Typography } from '@mui/material';
import { ArticleVideoPlayer } from 'components/player/ArticleVideoPlayer';
import { MediaPlayer } from 'components/player/MediaPlayer';
import { getFormattedTimeFromSec, isWebViewEnv } from 'helpers';
import AnandDate from 'helpers/AnandDate';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import type { ApplicationState, Article } from 'types';
import { allowsDevicePlayer, articleVideoPoster, isVideoSatsang } from 'video/articleVideo';
import { pauseInlineVideo } from 'video/playback';
import { ArticleTileHeader } from './ArticleTileHeader';
import type RecordList from 'components/RecordList';
import { LangMap } from 'config';

export function VideoArticleTile({ record, startPlaying = false, index = 0, listCtrl }: { record?: Article; index?: number; startPlaying?: boolean; listCtrl?: RecordList }) {
	const locale = useSelector((state: ApplicationState) => state.uxState.locale);
	const [active, setActive] = useState(startPlaying);
	const [error, setError] = useState('');
	const root = useRef<HTMLDivElement>(null);
	useLayoutEffect(() => {
		if (typeof ResizeObserver === 'undefined' && root.current && listCtrl) {
			listCtrl.setItemSize(index, Math.ceil(root.current.getBoundingClientRect().height) + 16);
		}
	});
	useLayoutEffect(() => {
		if (!root.current || !listCtrl) return;
		const measure = () => {
			if (root.current) listCtrl.setItemSize(index, Math.ceil(root.current.getBoundingClientRect().height) + 16);
		};
		measure();
		const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : undefined;
		observer?.observe(root.current);
		window.addEventListener('resize', measure);
		return () => { observer?.disconnect(); window.removeEventListener('resize', measure); };
	}, [record?.id, index, listCtrl, active, error]);
	if (!record) return <Skeleton variant="rectangular" sx={{ m: 1, aspectRatio: '16 / 9' }} />;
	const title = record.title[locale] || record.title.hi || record.title.en;
	const poster = articleVideoPoster(record);
	const hasMedia = !!(record.mediaUriR2 || record.mediaUri);

	return (
		<Card ref={root} sx={{ my: 1, width: 'calc(100% - 16px)', maxWidth: 720, mx: 'auto', overflow: active ? 'visible' : 'hidden' }} data-id={record.id} data-video-card>
			{active && hasMedia ? <ArticleVideoPlayer key={record.id} article={record} onDeactivate={() => setActive(false)} /> : (
				<Box sx={{ position: 'relative', aspectRatio: '16 / 9', bgcolor: '#18251f', overflow: 'hidden' }}>
					{poster && <Box component="img" src={poster} alt="" loading="lazy" sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
						onError={(event: React.SyntheticEvent<HTMLImageElement>) => { event.currentTarget.style.visibility = 'hidden'; }} />}
					<Button aria-label={'Play ' + title} disabled={!hasMedia} onClick={() => setActive(true)}
						sx={{ position: 'absolute', inset: 0, color: '#fff', fontSize: '2.5rem', textShadow: '0 1px 4px #000' }}>
						&#9654; {!poster && (isVideoSatsang(record) ? 'Video Satsang' : 'Video')}
					</Button>
					<Box sx={{ position: 'absolute', right: 8, bottom: 8, bgcolor: '#000b', color: '#fff', px: 0.75, borderRadius: 0.5 }}>
						{getFormattedTimeFromSec(record.mediaLength || 0)}
					</Box>
				</Box>
			)}
			<ArticleTileHeader record={record} locale={locale} subtitle={record.author || record.group} />
			<CardContent sx={{ pt: 1, '&:last-child': { pb: 2.5 } }}>
				<Typography variant="body2" color="text.secondary" noWrap>
					{new AnandDate(record.creationTime ?? record.publishTime).format('Do MMM, YYYY')} | {LangMap[record.lang] ?? record.lang}
				</Typography>
				{isWebViewEnv() && allowsDevicePlayer(record) && hasMedia && <Button onClick={() => {
					try {
						pauseInlineVideo();
						MediaPlayer.instance().playVideoInDevice(record);
					} catch (failure) {
						setError(failure instanceof Error ? failure.message : String(failure));
					}
				}}>Open in app player</Button>}
				{!hasMedia && <Alert severity="warning">This video is unavailable.</Alert>}
				{error && <Alert severity="error">{error}</Alert>}
			</CardContent>
		</Card>
	);
}
